import { Box } from "@mui/material";
import React, { FC, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { SectionHeader } from "@/app2/pages/HomePage/Components/SectionHeader";
import { getFromLocalStorage, setInLocalStorage } from "@/utils/common-utilities";
import type { LearnerAssignedAnnouncementDto } from "@/app2/shared/data/gen";
import { App2AnnouncementCard } from "./App2AnnouncementCard";

const useStyles = makeStyles()((theme) => ({
	container: {
		width: "100%",
	},
	scrollContainer: {
		display: "flex",
		flexWrap: "nowrap",
		overflowX: "auto",
		gap: "10px",
		paddingBottom: theme.spacing(2),
		scrollbarWidth: "none",
		"&::-webkit-scrollbar": {
			display: "none",
		},
		cursor: "grab",
		"&:active": {
			cursor: "grabbing",
		},
		userSelect: "none",
	},
	cardsContainer: {
		position: "relative",
		width: "100%",
	},
	gradientOverlayRight: {
		position: "absolute",
		top: 0,
		right: 0,
		width: "40px",
		height: "100%",
		background:
			"linear-gradient(to right, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1,
	},
	gradientOverlayLeft: {
		position: "absolute",
		top: 0,
		left: 0,
		width: "40px",
		height: "100%",
		background:
			"linear-gradient(to left, rgba(14, 14, 14, 0) 0%, rgba(14, 14, 14, 0.3) 50%, rgba(14, 14, 14, 0.7) 80%, #0E0E0E 100%)",
		pointerEvents: "none",
		zIndex: 1,
	},
}));

const DISMISSED_ANNOUNCEMENTS_KEY = "dismissedAnnouncements";

interface IProps {
	announcementsData: LearnerAssignedAnnouncementDto[];
	onComplete: (slug: string) => void;
}

export const App2AnnouncementsSection: FC<IProps> = ({ announcementsData, onComplete }) => {
	const { classes } = useStyles();
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const SCROLL_AMOUNT = 330;
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [dismissedIds, setDismissedIds] = useState<number[]>(() => {
		return getFromLocalStorage<number[]>(DISMISSED_ANNOUNCEMENTS_KEY) || [];
	});
	const [dismissingIds, setDismissingIds] = useState<number[]>([]);

	const announcementList = announcementsData;

	const scrollLeft = (): void => {
		scrollRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: "smooth" });
	};

	const scrollRight = (): void => {
		scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
	};

	const updateScrollButtons = (): void => {
		const el = scrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
	};

	const handleDismiss = (id: number): void => {
		setDismissingIds((prev) => [...prev, id]);
		setTimeout(() => {
			setDismissedIds((prev) => {
				if (!prev.includes(id)) {
					const updated = [...prev, id];
					setInLocalStorage(DISMISSED_ANNOUNCEMENTS_KEY, updated);
					return updated;
				}
				return prev;
			});
			setDismissingIds((prev) => prev.filter((d) => d !== id));
		}, 300);
	};

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		updateScrollButtons();
		el.addEventListener("scroll", updateScrollButtons);
		window.addEventListener("resize", updateScrollButtons);
		return () => {
			el.removeEventListener("scroll", updateScrollButtons);
			window.removeEventListener("resize", updateScrollButtons);
		};
	}, [announcementList]);

	const visible = announcementList.filter(
		(item) => item.complete === false && !dismissedIds.includes(item.id),
	);

	return (
		<Box
			className={classes.container}
			aria-label="Announcements section"
			data-test-id="app2-announcements-section"
		>
			<SectionHeader
				onScrollLeft={scrollLeft}
				onScrollRight={scrollRight}
				canScrollLeft={canScrollLeft}
				canScrollRight={canScrollRight}
				aria-label="Announcements navigation controls"
				data-test-id="app2-announcements-section-header"
			/>

			<Box
				className={classes.cardsContainer}
				aria-label="Announcements cards container"
				data-test-id="app2-announcements-cards-container"
			>
				{canScrollLeft && <Box className={classes.gradientOverlayLeft} />}
				<Box
					ref={scrollRef}
					className={classes.scrollContainer}
					role="region"
					aria-label="Scrollable announcements list"
					data-test-id="app2-announcements-scroll-container"
				>
					{visible.map((item) => (
						<App2AnnouncementCard
							key={item.id}
							announcement={item}
							onDismiss={handleDismiss}
							onComplete={onComplete}
							isDismissing={dismissingIds.includes(item.id)}
						/>
					))}
				</Box>
				{canScrollRight && <Box className={classes.gradientOverlayRight} />}
			</Box>
		</Box>
	);
};

export default App2AnnouncementsSection;
